
import React, { useState } from 'react';
import { User as UserIcon, Lock, AlertCircle, ExternalLink, Settings } from 'lucide-react';
import { auth, isConfigured } from '../lib/firebase';
import { UserRole } from '../types';
// Fix: Correct named imports for Firebase Auth
import { signInWithEmailAndPassword, setPersistence, browserLocalPersistence } from "firebase/auth";

interface LoginProps {
    onLogin: (user: any) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!isConfigured) {
      return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-8 text-center">
                <div className="mx-auto bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                    <Settings size={32} className="text-red-500" />
                </div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">Firebase設定が必要です</h2>
                <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                    アプリケーションを利用するには、Firebaseとの接続設定が必要です。<br/>
                    <code>lib/firebase.ts</code> ファイルを開き、
                    Firebaseコンソールから取得した設定値を入力してください。
                </p>
                <a 
                    href="https://console.firebase.google.com/" 
                    target="_blank" 
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition"
                >
                    Firebaseコンソールへ <ExternalLink size={16}/>
                </a>
            </div>
        </div>
      );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
        if (!auth) throw new Error("Auth service not initialized");
        
        // Explicitly set persistence to LOCAL (persists even after browser close)
        await setPersistence(auth, browserLocalPersistence);
        
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        if (user) {
          onLogin({
              id: user.uid,
              email: user.email || '',
              name: user.email?.split('@')[0] || 'ユーザー',
              role: UserRole.USER
          });
        }
    } catch (err: any) {
        console.error(err);
        let msg = 'ログインに失敗しました。';
        
        if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
            msg = 'メールアドレスまたはパスワードが間違っています。';
        } else if (err.code === 'auth/too-many-requests') {
            msg = 'ログイン試行回数が多すぎます。しばらく待ってから再試行してください。';
        } else if (err.code === 'auth/operation-not-allowed') {
            msg = 'Emailログインが無効です。Firebase Console > Authentication で有効にしてください。';
        } else if (err.code === 'auth/network-request-failed') {
            msg = 'ネットワークエラー。インターネット接続を確認してください。';
        }
        
        setError(msg);
        setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
        <div className="bg-[#2c3e50] p-8 text-center">
          <h1 className="text-2xl font-bold text-white mb-2">ガイアテック入金照合</h1>
        </div>

        <div className="p-8">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded text-sm">
              <div className="flex items-center gap-2 font-bold mb-1">
                 <AlertCircle size={16} /> エラー
              </div>
              {/* Ensure error is rendered as string */}
              {String(error)}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">メールアドレス</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserIcon size={18} className="text-gray-400" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors"
                  placeholder="name@gaiatec.jp"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">パスワード</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock size={18} className="text-gray-400" />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full bg-blue-600 text-white font-bold py-3 px-4 rounded hover:bg-blue-700 transition duration-200 flex items-center justify-center
                ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}
              `}
            >
              {isLoading ? (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
              ) : null}
              {isLoading ? 'ログイン中...' : 'ログイン'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;